import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { fbTrack, trackSignupTrial } from '@/lib/metaPixel';
import { getRefCode, getRefAsset, getRefCampaign, getGclid, fireResellerLead } from '@/lib/referral';

export const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUserData } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      const search = new URLSearchParams(window.location.search);
      const code = search.get('code'); const state = search.get('state');

      if (!sessionId && !(code && state)) {
        navigate('/login');
        return;
      }

      try {
        let signupPlan = null; let signupInterval = null;
        try { signupPlan = sessionStorage.getItem('veluro_signup_plan'); sessionStorage.removeItem('veluro_signup_plan'); } catch { /* ignore */ }
        try { signupInterval = sessionStorage.getItem('veluro_signup_interval'); sessionStorage.removeItem('veluro_signup_interval'); } catch { /* ignore */ }
        // Referral attribution for Google signups: the partner's code captured on their link rides along.
        const q = new URLSearchParams(sessionId ? { session_id: sessionId } : {});
        if (signupPlan) q.set('signup_plan', signupPlan);
        if (signupInterval) q.set('signup_interval', signupInterval);
        const refCode = getRefCode();
        if (refCode) {
          q.set('ref_code', refCode);
          const asset = getRefAsset(); if (asset) q.set('ref_asset', asset);
          const camp = getRefCampaign(); if (camp) q.set('ref_campaign', camp);
          const gclid = getGclid(); if (gclid) q.set('gclid', gclid);
        }
        const res = sessionId
          ? await api.get(`/auth/session?${q.toString()}`)
          : await api.post('/auth/google-login/exchange', { code, state, ...Object.fromEntries(q.entries()) });
        if (res.data?.token) localStorage.setItem('auth_token', res.data.token);
        setUserData(res.data);
        if (res.data?.is_new_user) { fbTrack('CompleteRegistration', { content_name: 'google' }); trackSignupTrial(res.data.user_id); fireResellerLead(); }

        // Clear hash / Google query params
        window.history.replaceState(null, '', sessionId ? window.location.pathname : '/home');

        const pl = String(res.data.plan_level || 'free');
        const isFreePublic = pl === 'free' && !res.data.is_referral_partner && res.data.role !== 'admin';
        if (isFreePublic && res.data.subscription_status !== 'trialing') {
          navigate('/upgrade', { state: { user: res.data } });
        } else if (res.data.has_onboarded) {
          navigate('/home', { state: { user: res.data } });
        } else {
          navigate('/onboarding', { state: { user: res.data } });
        }
      } catch (e) {
        // New Google account without a picked plan: nothing was created — send them to the plan picker.
        if (e?.response?.data?.detail?.code === 'plan_required') { navigate('/signup?pick_plan=1'); return; }
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, setUserData]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};
